import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { BookOpen, GraduationCap, User, Settings, LogOut, Menu, X, Beaker, Trophy, Presentation } from 'lucide-react';

const Navbar = () => {
  const { user, isAuthenticated, isAdmin, isTrainer, isTrainerOrAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'U';
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { to: '/courses', label: 'Courses', icon: BookOpen },
    { to: '/workshops', label: 'Workshops', icon: Presentation },
    { to: '/labs', label: 'Labs', icon: Beaker },
    { to: '/competitions', label: 'Competitions', icon: Trophy },
    ...(isAuthenticated ? [{ to: '/my-courses', label: 'My Learning', icon: GraduationCap }] : []),
  ];

  return (
    <header className="sticky top-0 z-50 glass-nav">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 font-heading font-bold text-xl text-primary hover:opacity-80 transition-opacity"
            data-testid="nav-logo"
          >
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            PluralSkill
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${isActive(link.to)
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                <link.icon className="w-4 h-4" />
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-10 w-10 rounded-full"
                    data-testid="user-menu-trigger"
                  >
                    <Avatar className="h-10 w-10 border-2 border-primary/20">
                      <AvatarFallback className="bg-primary text-white font-medium">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center gap-3 p-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary text-white">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">
                        {user?.first_name} {user?.last_name}
                      </span>
                      <span className="text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate('/profile')}
                    data-testid="menu-profile"
                    className="cursor-pointer"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate('/my-courses')}
                    data-testid="menu-my-courses"
                    className="cursor-pointer"
                  >
                    <GraduationCap className="mr-2 h-4 w-4" />
                    My Learning
                  </DropdownMenuItem>
                  {isTrainerOrAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      {isTrainer && !isAdmin && (
                        <DropdownMenuItem
                          onClick={() => navigate('/trainer-dashboard')}
                          data-testid="menu-trainer"
                          className="cursor-pointer"
                        >
                          <Presentation className="mr-2 h-4 w-4" />
                          Trainer Dashboard
                        </DropdownMenuItem>
                      )}
                      {isAdmin && (
                        <>
                          <DropdownMenuItem
                            onClick={() => navigate('/trainer-dashboard')}
                            data-testid="menu-trainer"
                            className="cursor-pointer"
                          >
                            <Presentation className="mr-2 h-4 w-4" />
                            Trainer Dashboard
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => navigate('/admin')}
                            data-testid="menu-admin"
                            className="cursor-pointer"
                          >
                            <Settings className="mr-2 h-4 w-4" />
                            Admin Dashboard
                          </DropdownMenuItem>
                        </>
                      )}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    data-testid="menu-logout"
                    className="cursor-pointer text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  data-testid="nav-login"
                >
                  Log in
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="rounded-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20"
                  data-testid="nav-signup"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg ${isActive(link.to)
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted'
                    }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
              {!isAuthenticated ? (
                <div className="flex flex-col gap-2 pt-4 border-t mt-2">
                  <Button variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                    Log in
                  </Button>
                  <Button onClick={() => { navigate('/signup'); setMobileMenuOpen(false); }}>
                    Get Started
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2 pt-4 border-t mt-2">
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                  >
                    <User className="w-5 h-5" />
                    Profile
                  </Link>
                  {isTrainerOrAdmin && (
                    <Link
                      to="/trainer-dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                      data-testid="mobile-trainer-link"
                    >
                      <Presentation className="w-5 h-5" />
                      Trainer Dashboard
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-muted"
                    >
                      <Settings className="w-5 h-5" />
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-destructive/10 text-destructive"
                  >
                    <LogOut className="w-5 h-5" />
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
